import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApiClient } from '../lib/apiClient';
import { formatMoney as formatOrgMoney } from '../i18n/orgFormat';

type PaymentStatus = 'UNPAID' | 'DUE_AT_PARENT' | 'PAID_AT_PARENT' | 'BOOKING_DEPOSIT' | 'PAYMENT_HOLD' | 'PART_PAID' | 'PACKAGE_REDEEMED' | 'COMPLIMENTARY' | 'WAIVED' | 'NOT_REQUIRED';
type ServicePackage = { id: string; serviceName: string; remainingQuantity: number; expiresAt?: string | null; status: string };
type PaymentRegistrationPayload = {
  appointment: {
    id: string;
    patient: { id: string; name: string };
    serviceDisplayName: string;
    appointmentType: { id: string; name: string; price: number; currency?: string | null };
    billedItems?: Array<{ serviceId?: string | null; appointmentTypeId?: string | null; description: string; quantity: number; unitPrice: number; total: number }>;
  };
  registration?: {
    paymentStatus?: PaymentStatus;
    expectedAmount?: number;
    discount?: number;
    tax?: number;
    finalAmount?: number;
    paidAmount?: number;
    paymentHoldReference?: string | null;
    externalReceiptReference?: string | null;
    notes?: string | null;
    invoiceNumber?: string | null;
  } | null;
};

const paymentStatuses: PaymentStatus[] = ['UNPAID', 'DUE_AT_PARENT', 'PAID_AT_PARENT', 'BOOKING_DEPOSIT', 'PAYMENT_HOLD', 'PART_PAID', 'PACKAGE_REDEEMED', 'COMPLIMENTARY', 'WAIVED', 'NOT_REQUIRED'];
const paymentStatusLabels: Record<PaymentStatus, string> = {
  UNPAID: 'Payment due',
  DUE_AT_PARENT: 'Pay on invoice',
  PAID_AT_PARENT: 'Already paid on invoice',
  BOOKING_DEPOSIT: 'Booking deposit paid',
  PAYMENT_HOLD: 'Payment on hold',
  PART_PAID: 'Part-paid invoice',
  PACKAGE_REDEEMED: 'Covered by package',
  COMPLIMENTARY: 'Complimentary',
  WAIVED: 'Waived',
  NOT_REQUIRED: 'No payment required',
};

function extractApiErrorMessage(raw: string, fallback: string) {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.message === 'string') return parsed.message;
    if (typeof parsed === 'string') return parsed;
  } catch {}
  return raw?.trim() || fallback;
}

function formatPaymentStatus(status: PaymentStatus) {
  return paymentStatusLabels[status];
}

function formatMoney(value: number) {
  return formatOrgMoney(Number(value || 0));
}

export default function ExternalPaymentRegistrationPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchJson, fetchApi } = useApiClient();
  const promotionSectionRef = useRef<HTMLDivElement | null>(null);
  const packageSectionRef = useRef<HTMLDivElement | null>(null);
  const paymentActionsRef = useRef<HTMLDivElement | null>(null);
  const [appointmentName, setAppointmentName] = useState('');
  const [serviceDisplayName, setServiceDisplayName] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [billedItems, setBilledItems] = useState<Array<{ serviceId?: string | null; appointmentTypeId?: string | null; description: string; quantity: number; unitPrice: number; total: number }>>([]);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('UNPAID');
  const [expectedAmount, setExpectedAmount] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [finalAmount, setFinalAmount] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [paymentHoldReference, setPaymentHoldReference] = useState('');
  const [promotionCode, setPromotionCode] = useState('');
  const [promotionRedemptionId, setPromotionRedemptionId] = useState('');
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const [packageRedemptionId, setPackageRedemptionId] = useState('');
  const [externalReceiptReference, setExternalReceiptReference] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemingPackage, setRedeemingPackage] = useState(false);
  const [error, setError] = useState('');
  const focusedSection = searchParams.get('focus');

  const invoiceTotal = Math.max(0, expectedAmount + tax - discount);
  const remainingBalance = ['BOOKING_DEPOSIT', 'PART_PAID', 'PAYMENT_HOLD'].includes(paymentStatus)
    ? Math.max(0, invoiceTotal - paidAmount)
    : Math.max(0, finalAmount - paidAmount);

  function setExpectedAmountAndTotal(value: number) {
    setExpectedAmount(value);
    setFinalAmount(Math.max(0, value + tax - discount));
  }

  function setDiscountAndTotal(value: number) {
    setDiscount(value);
    setFinalAmount(Math.max(0, expectedAmount + tax - value));
  }

  function setTaxAndTotal(value: number) {
    setTax(value);
    setFinalAmount(Math.max(0, expectedAmount + value - discount));
  }

  function applyPaymentPreset(status: PaymentStatus) {
    const total = Math.max(0, expectedAmount + tax - discount);
    setPaymentStatus(status);
    if (status === 'DUE_AT_PARENT') {
      setFinalAmount(total);
      setPaidAmount(0);
      setNotes((prev) => prev || 'Appointment invoice raised from calendar.');
    } else if (status === 'BOOKING_DEPOSIT') {
      const deposit = Number((total * 0.2).toFixed(2));
      setFinalAmount(total);
      setPaidAmount(deposit);
      setNotes((prev) => prev || `Booking deposit collected: ${formatMoney(deposit)}.`);
    } else if (status === 'PAYMENT_HOLD') {
      setFinalAmount(total);
      setPaidAmount(0);
      setPaymentHoldReference((prev) => prev || `HOLD-${id.slice(-6).toUpperCase()}`);
      setNotes((prev) => prev || 'Payment placed on hold from calendar booking.');
    } else if (status === 'PART_PAID') {
      const partial = Number((total / 2).toFixed(2));
      setFinalAmount(total);
      setPaidAmount(partial);
      setNotes((prev) => prev || `Part-paid invoice from calendar: ${formatMoney(partial)} received.`);
    }
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchJson<PaymentRegistrationPayload>(`/appointments/${id}/payment-registration`)
      .then(async (payload) => {
        if (!active) return;
        const resolvedServiceName = payload.appointment.serviceDisplayName || payload.appointment.appointmentType.name;
        setAppointmentName(`${payload.appointment.patient.name} · ${resolvedServiceName}`);
        setServiceDisplayName(resolvedServiceName);
        const registration = payload.registration;
        const defaultAmount = Number(payload.appointment.appointmentType.price || 0);
        setInvoiceNumber(registration?.invoiceNumber || 'Draft invoice');
        setBilledItems((payload.appointment.billedItems || []).map((item) => ({
          serviceId: item.serviceId ?? null,
          appointmentTypeId: item.appointmentTypeId ?? payload.appointment.appointmentType.id,
          description: item.description,
          quantity: Number(item.quantity || 1),
          unitPrice: Number(item.unitPrice || 0),
          total: Number(item.total || 0),
        })));
        setPaymentStatus(registration?.paymentStatus || 'UNPAID');
        setExpectedAmount(Number(registration?.expectedAmount ?? defaultAmount));
        setDiscount(Number(registration?.discount ?? 0));
        setTax(Number(registration?.tax ?? 0));
        setFinalAmount(Number(registration?.finalAmount ?? defaultAmount));
        setPaidAmount(Number(registration?.paidAmount ?? (registration?.paymentStatus === 'PAID_AT_PARENT' ? registration?.finalAmount ?? defaultAmount : 0)));
        setPaymentHoldReference(registration?.paymentHoldReference || '');
        setExternalReceiptReference(registration?.externalReceiptReference || '');
        setNotes(registration?.notes || '');
        const packagePayload = await fetchJson<{ data: ServicePackage[] }>(`/packages?patientId=${encodeURIComponent(payload.appointment.patient.id)}&appointmentTypeId=${encodeURIComponent(payload.appointment.appointmentType.id)}`);
        if (active) {
          const usable = (packagePayload.data || []).filter((pkg) => pkg.status === 'ACTIVE' && Number(pkg.remainingQuantity || 0) > 0);
          setPackages(usable);
          setSelectedPackageId(usable[0]?.id || '');
        }
      })
      .catch((err) => {
        if (active) setError(err instanceof Error ? err.message : 'Unable to load appointment payment.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchJson, id]);

  useEffect(() => {
    if (loading) return;
    if (focusedSection === 'promotion') {
      promotionSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (focusedSection === 'package') {
      packageSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (focusedSection === 'invoice') applyPaymentPreset('DUE_AT_PARENT');
    if (focusedSection === 'deposit') applyPaymentPreset('BOOKING_DEPOSIT');
    if (focusedSection === 'hold') applyPaymentPreset('PAYMENT_HOLD');
    if (focusedSection === 'partial') applyPaymentPreset('PART_PAID');
    if (['invoice', 'deposit', 'hold', 'partial'].includes(String(focusedSection))) {
      paymentActionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusedSection, loading]);

  async function save() {
    setSaving(true);
    setError('');
    try {
      const response = await fetchApi(`/appointments/${id}/payment-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentStatus, expectedAmount, discount, tax, finalAmount, paidAmount, paymentHoldReference: paymentHoldReference || undefined, promotionRedemptionId: promotionRedemptionId || undefined, packageRedemptionId: packageRedemptionId || undefined, externalReceiptReference, notes }),
      });
      if (!response.ok) throw new Error(extractApiErrorMessage(await response.text(), 'Unable to save appointment payment.'));
      navigate('/appointments');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save appointment payment.');
    } finally {
      setSaving(false);
    }
  }

  async function redeemPackage() {
    if (!selectedPackageId) return;
    setRedeemingPackage(true);
    setError('');
    try {
      const payload = await fetchJson<{ id: string; remainingQuantity: number }>(`/packages/${selectedPackageId}/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentId: id, quantity: 1 }),
      });
      setPackageRedemptionId(payload.id);
      setPaymentStatus('PACKAGE_REDEEMED');
      setDiscount(0);
      setTax(0);
      setFinalAmount(0);
      setPackages((prev) => prev.map((pkg) => (pkg.id === selectedPackageId ? { ...pkg, remainingQuantity: payload.remainingQuantity } : pkg)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to redeem package.');
    } finally {
      setRedeemingPackage(false);
    }
  }

  async function redeemPromotion() {
    setRedeeming(true);
    setError('');
    try {
      const payload = await fetchJson<{ id: string; code: string; discountAmount: number }>('/promotions/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: promotionCode,
          appointmentId: id,
          subtotal: expectedAmount,
          items: billedItems.map((item) => ({
            serviceId: item.serviceId || undefined,
            appointmentTypeId: item.appointmentTypeId || undefined,
            description: item.description,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            total: item.total,
          })),
        }),
      });
      const discountAmount = Number(payload.discountAmount || 0);
      setPromotionRedemptionId(payload.id);
      setPromotionCode(payload.code);
      setDiscount(discountAmount);
      setFinalAmount(Math.max(0, expectedAmount + tax - discountAmount));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to redeem promotion.');
    } finally {
      setRedeeming(false);
    }
  }

  if (loading) return <div className="p-6 text-slate-600">Loading appointment payment...</div>;

  return (
    <div className="max-w-2xl space-y-5 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Billing and payments</p>
          <h1 className="text-2xl font-semibold text-slate-900">{appointmentName}</h1>
          <p className="mt-2 text-sm text-slate-600">Apply offers, redeem packages, and save the payment outcome for this appointment.</p>
        </div>
        <Link to="/appointments" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700">
          <ArrowLeft size={16} />
          Back to appointments
        </Link>
      </div>
      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
        <div ref={paymentActionsRef} className={`grid gap-2 rounded-md border p-2 sm:grid-cols-2 ${['invoice', 'deposit', 'hold', 'partial'].includes(String(focusedSection)) ? 'border-lime-300 ring-2 ring-lime-100' : 'border-transparent'}`}>
          <button type="button" onClick={() => applyPaymentPreset('DUE_AT_PARENT')} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Appointment Invoice
            <span className="block text-xs font-normal text-slate-500">Raise the full appointment invoice.</span>
          </button>
          <button type="button" onClick={() => applyPaymentPreset('BOOKING_DEPOSIT')} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Booking Deposits
            <span className="block text-xs font-normal text-slate-500">Record a deposit during booking.</span>
          </button>
          <button type="button" onClick={() => applyPaymentPreset('PAYMENT_HOLD')} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Payment Hold
            <span className="block text-xs font-normal text-slate-500">Place this appointment payment on hold.</span>
          </button>
          <button type="button" onClick={() => applyPaymentPreset('PART_PAID')} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Part-Paid Invoice
            <span className="block text-xs font-normal text-slate-500">Raise an invoice with partial payment.</span>
          </button>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-medium">Service</span>
            <span>{serviceDisplayName || 'Unspecified service'}</span>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span>Invoice</span>
            <span>{invoiceNumber || 'Draft invoice'}</span>
          </div>
          {billedItems.length ? (
            <div className="mt-3 space-y-1 border-t border-slate-200 pt-3 text-xs text-slate-600">
              {billedItems.map((item, index) => (
                <div key={`${item.description}-${index}`} className="flex items-center justify-between gap-3">
                  <span>{item.description} x{item.quantity}</span>
                  <span>{formatMoney(item.total)}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Payment status</span>
          <select className="w-full rounded-md border border-slate-300 px-3 py-2" value={paymentStatus} onChange={(event) => setPaymentStatus(event.target.value as PaymentStatus)}>
            {paymentStatuses.map((status) => <option key={status} value={status}>{formatPaymentStatus(status)}</option>)}
          </select>
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Appointment amount</span>
            <input className="w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={0} value={expectedAmount} onChange={(event) => setExpectedAmountAndTotal(Number(event.target.value))} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Discount</span>
            <input className="w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={0} value={discount} onChange={(event) => setDiscountAndTotal(Number(event.target.value))} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Tax</span>
            <input className="w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={0} value={tax} onChange={(event) => setTaxAndTotal(Number(event.target.value))} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Amount to save</span>
            <input className="w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={0} value={finalAmount} onChange={(event) => setFinalAmount(Number(event.target.value))} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Paid or deposit amount</span>
            <input className="w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={0} value={paidAmount} onChange={(event) => setPaidAmount(Number(event.target.value))} />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Payment hold reference</span>
            <input className="w-full rounded-md border border-slate-300 px-3 py-2" value={paymentHoldReference} onChange={(event) => setPaymentHoldReference(event.target.value)} />
          </label>
        </div>
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <div className="flex items-center justify-between gap-3">
            <span>Invoice total</span>
            <strong>{formatMoney(invoiceTotal)}</strong>
          </div>
          <div className="mt-1 flex items-center justify-between gap-3 text-xs text-slate-500">
            <span>Remaining balance</span>
            <span>{formatMoney(remainingBalance)}</span>
          </div>
        </div>
        <div ref={promotionSectionRef} className={`rounded-md border bg-slate-50 p-3 ${focusedSection === 'promotion' ? 'border-lime-300 ring-2 ring-lime-100' : 'border-slate-200'}`}>
          <span className="mb-1 block text-sm font-medium text-slate-700">Offer code</span>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input className="w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={promotionCode} onChange={(event) => setPromotionCode(event.target.value)} />
            <button type="button" disabled={redeeming || !promotionCode.trim()} onClick={() => void redeemPromotion()} className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-800 ring-1 ring-slate-300 disabled:opacity-50">
              {redeeming ? 'Applying...' : 'Apply offer'}
            </button>
          </div>
          {promotionRedemptionId ? <p className="mt-2 text-xs text-emerald-700">Offer redeemed and ready to link to this payment registration.</p> : null}
        </div>
        <div ref={packageSectionRef} className={`rounded-md border bg-slate-50 p-3 ${focusedSection === 'package' ? 'border-lime-300 ring-2 ring-lime-100' : 'border-slate-200'}`}>
          <span className="mb-1 block text-sm font-medium text-slate-700">Package to redeem</span>
          <div className="flex flex-col gap-2 sm:flex-row">
            <select className="w-full rounded-md border border-slate-300 px-3 py-2" value={selectedPackageId} onChange={(event) => setSelectedPackageId(event.target.value)}>
              <option value="">No eligible package for this appointment</option>
              {packages.map((pkg) => <option key={pkg.id} value={pkg.id}>{pkg.serviceName} · {pkg.remainingQuantity} remaining{pkg.expiresAt ? ` · expires ${new Date(pkg.expiresAt).toLocaleDateString()}` : ''}</option>)}
            </select>
            <button type="button" disabled={redeemingPackage || !selectedPackageId || Boolean(packageRedemptionId)} onClick={() => void redeemPackage()} className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-slate-800 ring-1 ring-slate-300 disabled:opacity-50">
              {redeemingPackage ? 'Redeeming...' : packageRedemptionId ? 'Redeemed' : 'Redeem package'}
            </button>
          </div>
          {packageRedemptionId ? <p className="mt-2 text-xs text-emerald-700">Package redeemed and ready to link to this payment registration.</p> : null}
        </div>
        {paymentStatus === 'PAYMENT_HOLD' && paymentHoldReference ? (
          <button
            type="button"
            onClick={() => {
              setPaymentStatus('PART_PAID');
              setPaymentHoldReference('');
              setNotes((prev) => [prev, 'Held payment resumed for processing.'].filter(Boolean).join('\n'));
            }}
            className="w-full rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
          >
            Resume Held Payment
          </button>
        ) : null}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Receipt or reference number</span>
          <input className="w-full rounded-md border border-slate-300 px-3 py-2" placeholder="Cash receipt, bank transfer, card terminal, or invoice reference" value={externalReceiptReference} onChange={(event) => setExternalReceiptReference(event.target.value)} />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700">Notes</span>
          <textarea className="min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
        <button type="button" disabled={saving} onClick={() => void save()} className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          <CheckCircle2 size={16} />
          {saving ? 'Saving...' : 'Save payment and return to appointments'}
        </button>
      </div>
    </div>
  );
}
